import * as React from "react";
import { createContext, ReactNode, useCallback, useState } from "react";

export interface ModalConfig {
  title?: string | ReactNode;
  content?: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void | Promise<void>;
  onCancel?: () => void;
  onClose?: () => void;
  modalType?: "alert" | "modal";
  confirmButtonProps?: Record<string, any>;
  cancelButtonProps?: Record<string, any>;
  customActions?: ReactNode;
  hideCancel?: boolean;
  hideConfirm?: boolean;
  showDismiss?: boolean;
}

interface ModalState {
  isOpen: boolean;
  config: ModalConfig | null;
}

interface ModalContextValue {
  isOpen: boolean;
  config: ModalConfig | null;
  openModal: (config: ModalConfig) => void;
  closeModal: () => void;
}

export const ModalContext = createContext<ModalContextValue | null>(null);

interface ModalProviderProps {
  children: ReactNode;
}

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [modalState, setModalState] = useState<ModalState>({
    isOpen: false,
    config: null,
  });

  const openModal = useCallback((config: ModalConfig) => {
    setModalState({
      isOpen: true,
      config,
    });
  }, []);

  const closeModal = useCallback(() => {
    setModalState((prev) => ({
      ...prev,
      isOpen: false,
    }));
    // Clear config after animation completes
    setTimeout(() => {
      setModalState({
        isOpen: false,
        config: null,
      });
    }, 200);
  }, []);

  const value: ModalContextValue = {
    isOpen: modalState.isOpen,
    config: modalState.config,
    openModal,
    closeModal,
  };

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};
